<template>
  <div class="container">
    <div class="section users-view">
      <div class="content-header">
        <div>
          <h1 class="title is-3">Usuarios</h1>
          <p class="subtitle is-6">
            Administra las personas con acceso a la plataforma y sus permisos
          </p>
        </div>
      </div>

      <div class="columns is-vcentered filter-section">
        <div class="column search-column">
          <b-field>
            <b-input
              v-model="searchTerm"
              placeholder="Buscar por nombre, correo o empresa..."
              name="search"
              type="search"
              icon="magnify"
              maxlength="100"
              :has-counter="false"
              @input="onSearchInput"
            ></b-input>
          </b-field>
        </div>

        <div class="column"></div>

        <div class="column is-narrow">
          <b-dropdown
            v-model="claimsFilter"
            multiple
            aria-role="list"
            class="permission-filter-dropdown"
            @input="onClaimsChange"
          >
            <template #trigger="{ active }">
              <b-button
                :icon-right="active ? 'menu-up' : 'menu-down'"
                class="permission-filter-trigger"
              >
                <b-icon
                  icon="filter-variant"
                  size="is-small"
                  class="filter-icon"
                ></b-icon>
                <span class="filter-label">Permiso:</span>
                <span v-if="!claimsFilter.length" class="has-text-grey">
                  Todos
                </span>
                <span
                  v-else-if="claimsFilter.length === 1"
                  class="us-tag"
                  :class="'us-tag--' + claimsFilter[0]"
                >
                  {{ permissionLabels[claimsFilter[0]] }}
                </span>
                <span v-else class="has-text-weight-medium">
                  {{ claimsFilter.length }} permisos
                </span>
              </b-button>
            </template>

            <b-dropdown-item
              custom
              aria-role="listitem"
              class="permission-all-item"
              :class="{ 'is-active': !claimsFilter.length }"
              @click.native="clearClaims"
            >
              <span class="has-text-grey">Todos los permisos</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-for="(label, key) in permissionLabels"
              :key="key"
              :value="key"
              aria-role="listitem"
            >
              <span class="us-tag" :class="'us-tag--' + key">{{ label }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div v-if="tokenUser && tokenUser.admin" class="column is-narrow">
          <router-link :to="{ name: 'adduser' }" class="button is-dark">
            <b-icon icon="plus" class="mr-2"></b-icon>
            <span>Crear usuario</span>
          </router-link>
        </div>
      </div>

      <div class="box ag-grid-container">
        <ag-grid-vue
          v-show="showGrid"
          id="table-users"
          class="ag-theme-material"
          :gridOptions="gridOptions"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          @grid-ready="onGridReady"
        >
        </ag-grid-vue>

        <div v-if="phase === 'empty'" class="state-block">
          <template v-if="!hasFilters">
            <p class="state-title">No hay usuarios</p>
          </template>
          <template v-else>
            <p class="state-title">
              No se encontraron usuarios para: {{ filtersDescription }}
            </p>
            <b-button type="is-dark" @click="clearFilters">
              Limpiar filtros
            </b-button>
          </template>
        </div>

        <div v-if="phase === 'error'" class="state-block">
          <p class="state-title">No se pudo cargar la lista de usuarios</p>
          <b-button type="is-dark" @click="reload">Reintentar</b-button>
        </div>

        <div v-if="phase === 'forbidden'" class="state-block">
          <p class="state-title">
            Tu cuenta no tiene permiso para ver usuarios
          </p>
        </div>

        <div v-if="showGrid && blockError" class="block-error-strip">
          <span>No se pudo cargar un bloque de usuarios</span>
          <b-button size="is-small" type="is-dark" @click="retryBlock">
            Reintentar
          </b-button>
        </div>

        <div v-show="showGrid" class="row-count-footer">
          <span v-if="total === null">Cargando usuarios...</span>
          <span v-else>Mostrando {{ loadedCount }} de {{ total }} usuarios</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { AgGridVue } from "ag-grid-vue";
import UserActions from "../components/UserActions.vue";
import { deleteUser, fetchUsersBlock, sendPasswordLink } from "../api";
import {
  PERMISSION_LABELS,
  buildUsersParams,
  getCompanyName,
  normalizeSearch,
  renderCompanyCell,
  renderPermissionsCell,
  renderUserCell,
} from "../helpers";

const BLOCK_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 400;

export default {
  name: "UsersList",
  components: {
    AgGridVue,
    /* eslint-disable-next-line vue/no-unused-components */
    userActions: UserActions,
  },
  data() {
    return {
      searchTerm: "",
      appliedSearch: "",
      claimsFilter: [],
      appliedClaims: [],
      phase: "idle",
      total: null,
      loadedBlocks: {},
      blockError: null,
      resendingIds: [],
      gridApi: null,
      generation: 0,
      searchTimer: null,
      gridOptions: {
        context: {
          componentParent: this,
        },
        rowModelType: "infinite",
        cacheBlockSize: BLOCK_SIZE,
        maxConcurrentDatasourceRequests: -1,
        blockLoadDebounceMillis: 100,
        infiniteInitialRowCount: 10,
        rowHeight: 56,
        suppressNoRowsOverlay: true,
        suppressCellFocus: true,
        suppressMovableColumns: true,
        tooltipShowDelay: 300,
      },
      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: false,
        suppressMenu: true,
      },
      columnDefs: [
        {
          headerName: "Usuario",
          flex: 2,
          cellStyle: { display: "flex", alignItems: "center" },
          cellRenderer: renderUserCell,
        },
        {
          headerName: "Empresa",
          flex: 1.2,
          cellStyle: { display: "flex", alignItems: "center" },
          tooltipValueGetter: (params) =>
            params.data ? getCompanyName(params.data) : "",
          cellRenderer: renderCompanyCell,
        },
        {
          headerName: "Permisos",
          flex: 2,
          cellStyle: { display: "flex", alignItems: "center" },
          cellRenderer: renderPermissionsCell,
        },
        {
          headerName: "Acciones",
          width: 150,
          headerClass: "users-header-right",
          suppressNavigable: true,
          cellStyle: {
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          },
          cellRenderer: "userActions",
        },
      ],
    };
  },
  computed: {
    ...mapState(["idtoken", "tokenUser"]),
    permissionLabels() {
      return PERMISSION_LABELS;
    },
    showGrid() {
      return (
        this.phase === "idle" ||
        this.phase === "loading" ||
        this.phase === "ready"
      );
    },
    loadedCount() {
      return Object.keys(this.loadedBlocks).reduce(
        (sum, key) => sum + this.loadedBlocks[key],
        0,
      );
    },
    hasFilters() {
      return !!this.appliedSearch || this.appliedClaims.length > 0;
    },
    filtersDescription() {
      const parts = [];
      if (this.appliedSearch) parts.push(`«${this.appliedSearch}»`);
      if (this.appliedClaims.length) {
        const labels = this.appliedClaims.map((key) => PERMISSION_LABELS[key]);
        parts.push(`Permiso: ${labels.join(", ")}`);
      }
      return parts.join(", ");
    },
  },
  watch: {
    idtoken() {
      this.tryInit();
    },
  },
  created() {
    if (this.idtoken) this.tryInit();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.generation += 1;
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.tryInit();
    },
    tryInit() {
      if (this.gridApi && this.idtoken && this.phase === "idle") {
        this.reload();
      }
    },
    reload() {
      this.generation += 1;
      this.loadedBlocks = {};
      this.total = null;
      this.blockError = null;
      this.phase = "loading";
      this.gridApi.setDatasource(this.buildDatasource());
      this.gridApi.ensureIndexVisible(0);
    },
    buildDatasource() {
      const generation = this.generation;
      const search = this.appliedSearch;
      const claims = this.appliedClaims.slice();
      return {
        getRows: (params) => this.loadBlock(params, generation, search, claims),
      };
    },
    async loadBlock(params, generation, search, claims) {
      const limit = params.endRow - params.startRow;
      try {
        const block = await fetchUsersBlock(
          buildUsersParams({
            startRow: params.startRow,
            limit,
            search,
            claims,
          }),
        );
        if (generation !== this.generation) {
          params.failCallback();
          return;
        }
        const lastRow =
          block.rows.length < limit
            ? params.startRow + block.rows.length
            : block.total;
        this.$set(this.loadedBlocks, params.startRow, block.rows.length);
        this.total = block.total;
        this.blockError = null;
        if (params.startRow === 0) {
          this.phase = block.total === 0 ? "empty" : "ready";
        }
        params.successCallback(block.rows, lastRow);
      } catch (err) {
        if (generation !== this.generation) {
          params.failCallback();
          return;
        }
        this.handleBlockError(err, params, generation, search, claims);
      }
    },
    handleBlockError(err, params, generation, search, claims) {
      console.log(err);
      const status = err && err.response && err.response.status;
      if (status === 401 || status === 403) {
        this.phase = "forbidden";
        params.failCallback();
        return;
      }
      if (params.startRow === 0) {
        this.phase = "error";
        params.failCallback();
        return;
      }
      this.blockError = { params, generation, search, claims };
    },
    retryBlock() {
      const pending = this.blockError;
      if (!pending) return;
      this.blockError = null;
      this.loadBlock(
        pending.params,
        pending.generation,
        pending.search,
        pending.claims,
      );
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applySearch, SEARCH_DEBOUNCE_MS);
    },
    applySearch() {
      const next = normalizeSearch(this.searchTerm);
      if (next === this.appliedSearch) return;
      this.appliedSearch = next;
      this.reload();
    },
    clearClaims() {
      if (!this.claimsFilter.length) return;
      this.claimsFilter = [];
      this.onClaimsChange();
    },
    onClaimsChange() {
      this.appliedClaims = this.claimsFilter.slice();
      this.reload();
    },
    clearFilters() {
      clearTimeout(this.searchTimer);
      this.searchTerm = "";
      this.appliedSearch = "";
      this.claimsFilter = [];
      this.appliedClaims = [];
      this.reload();
    },
    handleEdit(user) {
      this.$router.push({ name: "edituser", params: { id: user._id } });
    },
    async handleDelete(user) {
      const confirmation = await this.$swal({
        title: "¡Cuidado!",
        text: `¿Realmente desea eliminar al usuario "${user.displayName}"?`,
        icon: "warning",
        showCancelButton: true,
        cancelButtonColor: "#de5b5b",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmation || !confirmation.isConfirmed) return;
      try {
        await deleteUser(user._id);
      } catch (err) {
        console.log(err);
        this.$store.dispatch("notify", {
          message: "Hubo un problema no se pudo eliminar el usuario",
          type: "is-danger",
        });
        return;
      }
      this.$store.dispatch("notify", {
        message: "El usuario fue eliminado con éxito",
        type: "is-link",
      });
      if (this.total !== null) this.total = Math.max(this.total - 1, 0);
      if (this.gridApi) this.gridApi.refreshInfiniteCache();
    },
    async handleResend(user) {
      if (this.resendingIds.indexOf(user._id) > -1) return;
      const confirmation = await this.$swal({
        title: "Reenviar correo de contraseña",
        text: `Se enviará un correo para crear la contraseña a ${user.email}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Reenviar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmation || !confirmation.isConfirmed) return;
      if (this.resendingIds.indexOf(user._id) > -1) return;
      this.resendingIds.push(user._id);
      try {
        const body = await sendPasswordLink(user._id);
        if (body && body.emailSent === true) {
          this.$store.dispatch("notify", {
            message: `Correo de contraseña reenviado a ${user.email}`,
            type: "is-link",
          });
        } else {
          this.$store.dispatch("notify", {
            message: "No se pudo reenviar el correo. Inténtelo nuevamente",
            type: "is-danger",
            duration: 4000,
          });
        }
      } catch (err) {
        this.$store.dispatch("notify", {
          message: "No se pudo reenviar el correo. Inténtelo nuevamente",
          type: "is-danger",
          duration: 4000,
        });
      } finally {
        this.resendingIds = this.resendingIds.filter((id) => id !== user._id);
      }
    },
  },
};
</script>

<style src="../styles.css"></style>

<style>
@import "~ag-grid-community/styles/ag-grid.css";
@import "~ag-grid-community/styles/ag-theme-material.css";

.ag-root-wrapper {
  border-radius: 10px !important;
}

#table-users {
  width: 100%;
  height: calc(100vh - 360px);
  min-height: 420px;
}

#table-users .ag-row-selected::before {
  background-color: unset !important;
}

#table-users .ag-row {
  transition: background-color 0.15s ease;
}

#table-users .ag-row:hover {
  background-color: #f7f4fb;
}

#table-users .ag-cell {
  line-height: 1.3;
}

#table-users .ag-tooltip {
  background-color: #2b2b2b;
  color: #fff;
  border: 1px solid #6b2fae;
  border-radius: 8px;
  padding: 0.4rem 0.7rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

#table-users .user-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

#table-users .user-cell-text {
  min-width: 0;
}

#table-users .user-cell-name {
  font-weight: 600;
  color: #363636;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#table-users .user-cell-email {
  font-size: 0.813rem;
  color: #7a7a7a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#table-users .user-company {
  flex: 1;
  min-width: 0;
  color: #363636;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#table-users .user-tags {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.35rem;
  overflow: hidden;
}

#table-users .users-header-right .ag-header-cell-label {
  justify-content: flex-end;
}

.permission-filter-dropdown .permission-all-item {
  cursor: pointer;
}

#table-users .user-skeleton-bar {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: user-skeleton-pulse 1.2s ease-in-out infinite;
}

@keyframes user-skeleton-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.permission-filter-dropdown .dropdown-item {
  padding: 0.5rem 2rem 0.5rem 2rem;
  position: relative;
  background-color: transparent !important;
  color: inherit !important;
}

.permission-filter-dropdown .dropdown-item:hover {
  background-color: #f5f5f5 !important;
}

.permission-filter-dropdown .dropdown-item.is-active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: #7957d5;
}

.permission-filter-dropdown .dropdown-item.is-active::after {
  content: "\F012C";
  font-family: "Material Design Icons";
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #7957d5;
  font-size: 16px;
}

.permission-filter-trigger {
  min-width: 220px;
  justify-content: flex-start;
  gap: 0.4rem;
}

.permission-filter-trigger .filter-icon {
  color: #7a7a7a;
}

.permission-filter-trigger .filter-label {
  color: #7a7a7a;
  font-weight: 500;
  margin-right: 0.25rem;
}
</style>

<style scoped>
.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
}

.filter-section {
  margin-bottom: 1.5rem;
}

.search-column {
  max-width: 360px;
}

.ag-grid-container {
  padding: 0;
}

.row-count-footer {
  padding: 0.65rem 1rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.813rem;
  color: #7a7a7a;
  text-align: right;
}

.state-block {
  padding: 4rem 1rem;
  text-align: center;
}

.state-title {
  margin-bottom: 1rem;
  color: #7a7a7a;
  font-size: 1.1rem;
}

.block-error-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.6rem 1rem;
  border-top: 1px solid #f0f0f0;
  background-color: #fff5f5;
  color: #cc0f35;
  font-size: 0.875rem;
}
</style>



// WEBPACK FOOTER //
// src/components/users/views/Users.vue